import React, { useState } from 'react';
import { Reveal } from './ui/Reveal';
import { Button } from './ui/Button';
import { Mail, Phone, MapPin, Clock, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';
interface ContactSectionProps {
  showToast: (message: string, type: 'success' | 'warning') => void;
}
export function ContactSection({
  showToast
}: ContactSectionProps) {
  const {
    content
  } = useSiteContent();
  const contact = content.contact;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      showToast('Por favor completa todos los campos requeridos', 'warning');
      return;
    }
    showToast('Mensaje enviado. Te contactaremos pronto!', 'success');
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };
  return <section className="py-24 px-6 md:px-16 bg-sand" data-id="element-886">
      <div className="max-w-7xl mx-auto" data-id="element-887">
        <Reveal className="text-center mb-16" data-id="element-888">
          <span className="inline-block bg-sage-light/30 text-earth text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4" data-id="element-889">
            {contact.sectionLabel}
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-earth mb-4" data-id="element-890">
            {contact.sectionTitle}
          </h2>
          <p className="text-stone text-lg max-w-2xl mx-auto" data-id="element-891">
            {contact.sectionSubtitle}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-892">
          {/* Formulario de Contacto */}
          <Reveal delay={100} data-id="element-893">
            <div className="bg-white rounded-3xl p-8 shadow-sm" data-id="element-894">
              <h3 className="font-display text-2xl text-earth mb-6" data-id="element-895">
                Envíanos un mensaje
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-896">
                <div data-id="element-897">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-898">
                    Nombre completo *
                  </label>
                  <input type="text" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full p-3 border-2 border-mist rounded-xl text-sm outline-none transition-all focus:border-sage focus:ring-4 focus:ring-sage/10" placeholder="María González" data-id="element-899" />
                </div>

                <div data-id="element-900">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-901">
                    Email *
                  </label>
                  <input type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full p-3 border-2 border-mist rounded-xl text-sm outline-none transition-all focus:border-sage focus:ring-4 focus:ring-sage/10" placeholder="maria@ejemplo.com" data-id="element-902" />
                </div>

                <div data-id="element-903">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-904">
                    Teléfono (opcional)
                  </label>
                  <input type="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="w-full p-3 border-2 border-mist rounded-xl text-sm outline-none transition-all focus:border-sage focus:ring-4 focus:ring-sage/10" placeholder="+52 55 1234 5678" data-id="element-905" />
                </div>

                <div data-id="element-906">
                  <label className="block text-sm font-semibold text-earth mb-2" data-id="element-907">
                    Mensaje *
                  </label>
                  <textarea value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} rows={5} className="w-full p-3 border-2 border-mist rounded-xl text-sm outline-none transition-all focus:border-sage focus:ring-4 focus:ring-sage/10 resize-none" placeholder="¿En qué podemos ayudarte?" data-id="element-908" />
                </div>

                <Button type="submit" variant="sage" className="w-full" data-id="element-909">
                  Enviar Mensaje
                </Button>
              </form>
            </div>
          </Reveal>

          {/* Información de Contacto */}
          <Reveal delay={200} data-id="element-910">
            <div className="space-y-6" data-id="element-911">
              <div className="bg-white rounded-3xl p-8 shadow-sm" data-id="element-912">
                <h3 className="font-display text-2xl text-earth mb-6" data-id="element-913">
                  Información de Contacto
                </h3>

                <div className="space-y-4" data-id="element-914">
                  <div className="flex items-start gap-4" data-id="element-915">
                    <div className="w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center shrink-0" data-id="element-916">
                      <Mail className="text-sage" size={20} data-id="element-917" />
                    </div>
                    <div data-id="element-918">
                      <h4 className="font-semibold text-earth mb-1" data-id="element-919">Email</h4>
                      <a href={`mailto:${contact.email}`} className="text-sm text-stone hover:text-sage transition-colors" data-id="element-920">
                        {contact.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-921">
                    <div className="w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center shrink-0" data-id="element-922">
                      <Phone className="text-sage" size={20} data-id="element-923" />
                    </div>
                    <div data-id="element-924">
                      <h4 className="font-semibold text-earth mb-1" data-id="element-925">
                        Teléfono
                      </h4>
                      <a href={`tel:${contact.phone.replace(/\D/g, '')}`} className="text-sm text-stone hover:text-sage transition-colors" data-id="element-926">
                        {contact.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-927">
                    <div className="w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center shrink-0" data-id="element-928">
                      <MessageCircle className="text-sage" size={20} data-id="element-929" />
                    </div>
                    <div data-id="element-930">
                      <h4 className="font-semibold text-earth mb-1" data-id="element-931">
                        WhatsApp
                      </h4>
                      <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} className="text-sm text-stone hover:text-sage transition-colors" data-id="element-932">
                        {contact.whatsapp}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-933">
                    <div className="w-12 h-12 bg-sage/10 rounded-xl flex items-center justify-center shrink-0" data-id="element-934">
                      <Clock className="text-sage" size={20} data-id="element-935" />
                    </div>
                    <div data-id="element-936">
                      <h4 className="font-semibold text-earth mb-1" data-id="element-937">
                        Horario de Atención
                      </h4>
                      <p className="text-sm text-stone" data-id="element-938">
                        {contact.hoursWeekday}
                      </p>
                      <p className="text-sm text-stone" data-id="element-939">
                        {contact.hoursWeekend}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-earth text-cream rounded-3xl p-8 shadow-sm" data-id="element-940">
                <h3 className="font-display text-xl mb-4" data-id="element-941">Síguenos en Redes</h3>
                <div className="flex gap-3" data-id="element-942">
                  <a href="#" className="w-12 h-12 bg-cream/10 rounded-xl flex items-center justify-center hover:bg-sage transition-colors" data-id="element-943">
                    <Instagram size={20} data-id="element-944" />
                  </a>
                  <a href="#" className="w-12 h-12 bg-cream/10 rounded-xl flex items-center justify-center hover:bg-sage transition-colors" data-id="element-945">
                    <Facebook size={20} data-id="element-946" />
                  </a>
                  <a href="#" className="w-12 h-12 bg-cream/10 rounded-xl flex items-center justify-center hover:bg-sage transition-colors" data-id="element-947">
                    <MessageCircle size={20} data-id="element-948" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}